import { useState, useMemo } from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

const METRICS = ['mood', 'energy', 'confidence', 'focus', 'sleep_quality', 'performance_rating', 'stress'];
const LABELS = { mood: 'Mood', energy: 'Energy', confidence: 'Confidence', focus: 'Focus', sleep_quality: 'Sleep', performance_rating: 'Performance', stress: 'Stress' };

const PERIODS = [
  { key: '1w', label: 'This Week vs Last Week', current: 7, previous: 7 },
  { key: '2w', label: '2 Weeks vs Prior 2 Weeks', current: 14, previous: 14 },
  { key: '30d', label: '30 Days vs Prior 30 Days', current: 30, previous: 30 },
];

export default function WeeklyComparison({ individualTrends }) {
  const [period, setPeriod] = useState('1w');

  const comparison = useMemo(() => {
    if (!individualTrends || individualTrends.length < 2) return null;
    const sorted = [...individualTrends].sort((a, b) => new Date(b.date) - new Date(a.date));
    const p = PERIODS.find(p => p.key === period);
    const currentSlice = sorted.slice(0, p.current);
    const previousSlice = sorted.slice(p.current, p.current + p.previous);
    if (currentSlice.length === 0 || previousSlice.length === 0) return null;

    const avg = (arr, key) => arr.reduce((s, d) => s + (d[key] || 0), 0) / arr.length;

    return METRICS.map(key => {
      const curr = avg(currentSlice, key);
      const prev = avg(previousSlice, key);
      const diff = curr - prev;
      const isStress = key === 'stress';
      const improved = isStress ? diff < -0.3 : diff > 0.3;
      const declined = isStress ? diff > 0.3 : diff < -0.3;
      return { key, label: LABELS[key], current: curr.toFixed(1), previous: prev.toFixed(1), diff: diff.toFixed(1), improved, declined };
    });
  }, [individualTrends, period]);

  if (!comparison) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h2 className="text-lg font-bold text-gray-900 mb-2">Period Comparison</h2>
        <p className="text-sm text-gray-400">Need more data to compare periods</p>
      </div>
    );
  }

  const selectedPeriod = PERIODS.find(p => p.key === period);

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-testid="weekly-comparison">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-900">{selectedPeriod.label}</h2>
        <div className="flex gap-1" data-testid="comparison-period-toggle">
          {PERIODS.map(p => (
            <button key={p.key} onClick={() => setPeriod(p.key)}
              className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all ${
                period === p.key ? 'bg-emerald-600 text-white shadow-sm' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}>
              {p.key}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        {comparison.map(m => (
          <div key={m.key} className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
            <span className="text-sm text-gray-700 font-medium w-28">{m.label}</span>
            <div className="flex items-center gap-4 flex-1 justify-end">
              <span className="text-xs text-gray-400 w-12 text-right">{m.previous}</span>
              <span className="text-xs text-gray-400">→</span>
              <span className="text-sm font-bold text-gray-900 w-12 text-right">{m.current}</span>
              <div className={`flex items-center gap-0.5 w-16 justify-end text-xs font-bold ${
                m.improved ? 'text-emerald-600' : m.declined ? 'text-red-500' : 'text-gray-400'
              }`}>
                {m.improved ? <TrendingUp className="w-3.5 h-3.5" /> : m.declined ? <TrendingDown className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
                {m.diff > 0 ? '+' : ''}{m.diff}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
